import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  Briefcase,
  Building2,
  CheckCircle2,
  Home,
  Quote,
  Shield,
  Stethoscope,
  Target,
  TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";

const fade = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.12 },
  },
};

const audiences = [
  {
    id: "health-systems",
    icon: Building2,
    name: "Health Systems",
    subtitle: "Multi-hospital IDNs, 200+ beds",
    pains: [
      "Fragmented EHR and applications across acquired entities",
      "Rising infrastructure and licensing costs on thin margins",
      "Clinical-grade cyber risk across admin and clinical stacks",
    ],
    services: ["AI", "Healthcare IT", "Cyber", "Cloud", "Advisory"],
    stat: "15 hospitals · 15M+ patients",
  },
  {
    id: "physician-networks",
    icon: Stethoscope,
    name: "Physician Networks",
    subtitle: "Multi-specialty groups & ambulatory networks",
    pains: [
      "Local IT teams without enterprise-grade rigor",
      "EHR and application sprawl across merged practices",
      "Compliance exposure without a full in-house security bench",
    ],
    services: ["Healthcare IT", "Cyber", "Cloud"],
    stat: "250+ clinics supported",
  },
  {
    id: "msos",
    icon: Briefcase,
    name: "Managed Service Organizations",
    subtitle: "Back-office operators for multi-practice platforms",
    pains: [
      "Standardizing IT across heterogeneous practice groups",
      "Vendor contract consolidation and cost discipline",
      "Portfolio-wide cyber and HIPAA exposure",
    ],
    services: ["Advisory", "Healthcare IT", "Cloud", "Cyber"],
    stat: "Multi-entity cost programs",
  },
  {
    id: "home-health",
    icon: Home,
    name: "Home Health Companies",
    subtitle: "Home health, hospice, and in-home care networks",
    pains: [
      "Mobile workforce endpoint security and compliance",
      "EMR integrations across care sites and documentation flows",
      "Cloud-hosted clinical workflows under tight budget",
    ],
    services: ["Cyber", "Cloud", "Healthcare IT"],
    stat: "Mobile-first delivery",
  },
  {
    id: "behavioral-health",
    icon: Brain,
    name: "Behavioral Health Providers",
    subtitle: "Behavioral health networks & outpatient mental health",
    pains: [
      "HIPAA compliance across telehealth and digital channels",
      "Data privacy controls for sensitive patient records",
      "Lean IT teams supporting growing care volumes",
    ],
    services: ["Cyber", "Cloud", "Healthcare IT"],
    stat: "Compliance-grade ops",
  },
];

const coverage = [
  { value: "15", label: "Hospitals" },
  { value: "250+", label: "Clinics" },
  { value: "6", label: "States" },
  { value: "15M+", label: "Patients Served" },
];

const cases = [
  {
    id: "ransomware-recovery",
    label: "Cyber recovery",
    title: "From ransomware blackout to full clinical operations in 7 days",
    challenge:
      "A 300-bed Northern California health system went fully offline after a ransomware attack — every IT system down, losing millions in revenue daily.",
    solution:
      "Velozent stood up a 24/7 command center on-site within hours. New security controls were deployed, a clean digital perimeter rebuilt, and clinical operations restored in phases across the system.",
    results:
      "Full operations restored within 7 days. Millions in potential ransomware payments avoided. Patient confidence restored.",
    quote:
      "Velozent has a rare combination of healthcare operational know-how and security depth. No other team we called could have restored operations in that timeline.",
    attribution: "CEO · 300-bed Acute Care Health System",
    tag: "Cyber Security · Healthcare IT",
  },
  {
    id: "ma-turnaround",
    label: "M&A turnaround",
    title: "Reviving a struggling mid-west health system through M&A",
    challenge:
      "A healthcare investor group was evaluating the acquisition of a mid-west health system facing closure within weeks due to mounting losses.",
    solution:
      "Velozent's operators ran the M&A playbook — break-even analysis, IT cost rationalization, cloud-based system deployment in critical areas, and Day-1 post-close integration.",
    results:
      "Acquisition closed successfully. Financial turnaround achieved ahead of schedule with measurable cost discipline.",
    quote:
      "Velozent's healthcare M&A playbook was central to our turnaround strategy. They bring rare operator expertise that blends clinical, operational, and IT judgment.",
    attribution: "Managing Partner · Healthcare Investor Group",
    tag: "Advisory · Cloud · Healthcare IT",
  },
  {
    id: "cost-rationalization",
    label: "Cost rationalization",
    title: "$15M in first-year cost savings at a 400-bed health system",
    challenge:
      "A 400-bed Southern California health system had sprawling IT spend across managed services, Office 365, EMR, cloud, and security — with duplicated vendors and misaligned contracts.",
    solution:
      "Velozent ran a full IT cost and contract review, rationalized managed services, and executed a unified migration across O365, EMR optimization, cloud, and security — coordinated against clinical uptime schedules.",
    results:
      "$15M in first-year savings with zero clinical disruption. Uptime SLAs maintained throughout the full cutover.",
    quote:
      "We cut $15M in the first year without a single clinical disruption. That's what happens when your partner runs the playbook instead of selling it.",
    attribution: "CIO · 400-bed Southern California Health System",
    tag: "Advisory · Healthcare IT · Cloud · Cyber",
  },
];

export default function WhoWeServe() {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <Helmet>
        <title>
          Who We Serve | Velozent — Healthcare IT for Health Systems,
          Physician Networks, MSOs, Home Health & Behavioral Health
        </title>
        <meta
          name="description"
          content="Velozent serves health systems, physician networks, MSOs, home health, and behavioral health providers across 15 hospitals, 250+ clinics, and 6 states — delivered by former CIOs, CISOs, and CTOs."
        />
        <meta
          name="keywords"
          content="healthcare IT audiences, health systems, physician networks, MSOs, managed service organizations, home health IT, behavioral health IT, Velozent"
        />
        <meta
          property="og:title"
          content="Who We Serve | Velozent — Healthcare IT for Every Provider Type"
        />
        <meta
          property="og:description"
          content="Five healthcare buyer archetypes, one operator-led delivery model. Proven across 15 hospitals, 250+ clinics, and 6 states."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/who-we-serve" />
        <link rel="canonical" href="https://velozent.com/who-we-serve" />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 text-white lg:pb-20 lg:pt-24">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <motion.p
              variants={fade}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400"
            >
              Who we serve
            </motion.p>

            <motion.h1
              variants={fade}
              className="mx-auto mt-4 max-w-4xl text-balance text-[clamp(2rem,4.6vw,3.15rem)] font-extrabold leading-[1.08] tracking-tight text-white"
            >
              Built for the people accountable for{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                healthcare IT outcomes
              </span>
            </motion.h1>

            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-2xl text-[1.05rem] leading-relaxed text-white/75"
            >
              Health systems, physician networks, MSOs, home health, and
              behavioral health providers — served by former CIOs, CISOs, and
              CTOs who have sat in your seat.
            </motion.p>

            <motion.div
              variants={fade}
              className="mt-9 flex flex-wrap items-center justify-center gap-4"
            >
              <Button
                asChild
                size="lg"
                className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
                style={{ backgroundColor: "#e76d38" }}
              >
                <Link to="/contact">
                  Book Your 15-Minute Strategy Call
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <a
                href="#audiences"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
              >
                See audiences below
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            <motion.p
              variants={fade}
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40"
            >
              No obligation · No pitch deck · 15-minute call
            </motion.p>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-burnt-sienna-500/50 to-transparent" />
      </section>

      {/* ── Audience grid ────────────────────────────────────────────────── */}
      <section
        id="audiences"
        className="relative bg-white px-4 py-16 lg:px-8 lg:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              Audiences
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              Five healthcare buyer archetypes, one delivery model
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              We serve the executives and operators accountable for healthcare
              IT outcomes — not logos on a vendor slide. Each audience below
              comes to us with a distinct profile of pain and priorities.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3"
          >
            {audiences.map((a) => {
              const Icon = a.icon;
              return (
                <motion.article
                  key={a.id}
                  id={a.id}
                  variants={fade}
                  className="group flex flex-col rounded-2xl border border-shark-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-complement-300 hover:shadow-[0_22px_64px_-44px_rgba(231,109,56,0.55)]"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-complement-50 text-complement-700 ring-1 ring-complement-200">
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-[1.05rem] font-bold leading-snug text-shark-900 transition-colors group-hover:text-complement-700">
                        {a.name}
                      </h3>
                      <p className="mt-0.5 text-[0.82rem] leading-snug text-shark-500">
                        {a.subtitle}
                      </p>
                    </div>
                  </div>

                  <p className="mt-5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                    Why they come to us
                  </p>
                  <ul className="mt-2.5 space-y-2">
                    {a.pains.map((p) => (
                      <li
                        key={p}
                        className="flex items-start text-[0.86rem] leading-relaxed text-shark-700"
                      >
                        <CheckCircle2 className="mr-2.5 mt-0.5 h-4 w-4 flex-shrink-0 text-complement-500" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {a.services.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-shark-200 bg-shark-50/60 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-shark-600"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto">
                    <div className="mt-5 border-t border-shark-100 pt-4">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-complement-600">
                        Footprint
                      </p>
                      <p className="mt-1 text-[0.88rem] font-semibold text-shark-800">
                        {a.stat}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Coverage / reach band ────────────────────────────────────────── */}
      <section className="bg-shark-50/60 px-4 py-14 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
                National footprint
              </p>
              <h2 className="mt-3 text-[clamp(1.375rem,2.4vw,1.75rem)] font-extrabold leading-[1.15] tracking-tight text-shark-950">
                Operators based in Southern California, delivering nationwide
              </h2>
              <p className="mt-4 max-w-[520px] text-[0.95rem] leading-[1.65] text-shark-500">
                Our leadership team is former CIOs, CISOs, and CTOs who have
                devoted their careers to the intersection of healthcare and
                technology. Headquartered in Southern California, with
                delivery resources strategically placed across the country.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {coverage.map((c) => (
                <div
                  key={c.label}
                  className="rounded-xl border border-shark-200 bg-white px-4 py-5 text-center shadow-sm"
                >
                  <p className="text-[1.75rem] font-extrabold leading-none tracking-tight text-shark-950 sm:text-[2rem]">
                    {c.value}
                  </p>
                  <p className="mt-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-500">
                    {c.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Case studies ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-shark-950 px-4 py-20 text-white lg:px-8 lg:py-24">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_30%_0%,rgba(231,109,56,0.1),transparent_60%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[10%] top-[20%] h-[400px] w-[400px] rounded-full bg-complement-500/10 blur-[140px]"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400">
              Proof in production
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-white">
              Real operators. Real turnarounds.
            </h2>
            <div className="mt-6 h-px w-10 bg-complement-500" aria-hidden />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-white/70">
              Three engagements that capture the shape of our work —
              distressed acquisitions, catastrophic cyber events, and
              enterprise-wide cost programs. Each delivered by operators who
              owned the outcome end-to-end.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 space-y-6"
          >
            {cases.map((c) => (
              <motion.article
                key={c.id}
                id={c.id}
                variants={fade}
                className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
              >
                <div className="border-b border-white/10 px-6 py-5 sm:px-8">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-burnt-sienna-400/40 bg-burnt-sienna-500/10 px-3 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-burnt-sienna-300">
                      {c.label}
                    </span>
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/40">
                      {c.tag}
                    </span>
                  </div>
                  <h3 className="mt-3 max-w-3xl text-[1.2rem] font-bold leading-snug text-white sm:text-[1.35rem]">
                    {c.title}
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-px bg-white/10 lg:grid-cols-3">
                  <div className="bg-shark-950 p-6 sm:p-7">
                    <div className="flex items-center gap-2 text-burnt-sienna-400">
                      <Target className="h-4 w-4" strokeWidth={2.2} />
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]">
                        Challenge
                      </p>
                    </div>
                    <p className="mt-3 text-[0.9rem] leading-relaxed text-white/70">
                      {c.challenge}
                    </p>
                  </div>
                  <div className="bg-shark-950 p-6 sm:p-7">
                    <div className="flex items-center gap-2 text-burnt-sienna-400">
                      <Shield className="h-4 w-4" strokeWidth={2.2} />
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]">
                        Solution
                      </p>
                    </div>
                    <p className="mt-3 text-[0.9rem] leading-relaxed text-white/70">
                      {c.solution}
                    </p>
                  </div>
                  <div className="bg-shark-950 p-6 sm:p-7">
                    <div className="flex items-center gap-2 text-burnt-sienna-400">
                      <TrendingUp className="h-4 w-4" strokeWidth={2.2} />
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]">
                        Results
                      </p>
                    </div>
                    <p className="mt-3 text-[0.9rem] leading-relaxed text-white/70">
                      {c.results}
                    </p>
                  </div>
                </div>

                <figure className="relative border-t border-white/10 bg-gradient-to-br from-burnt-sienna-500/10 via-transparent to-transparent px-6 py-6 sm:px-8">
                  <span
                    className="pointer-events-none absolute left-4 top-2 select-none text-[4.5rem] font-serif leading-none text-burnt-sienna-400/20"
                    aria-hidden
                  >
                    &ldquo;
                  </span>
                  <blockquote className="relative pl-6">
                    <Quote
                      className="absolute -left-1 top-1 h-4 w-4 text-burnt-sienna-400 sm:hidden"
                      strokeWidth={2.2}
                    />
                    <p className="text-[0.98rem] italic leading-relaxed text-white/85 sm:text-[1.02rem]">
                      {c.quote}
                    </p>
                    <figcaption className="mt-3 text-[11px] font-bold uppercase tracking-[0.16em] text-burnt-sienna-400">
                      {c.attribution}
                    </figcaption>
                  </blockquote>
                </figure>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-t border-white/10 px-4 py-14 text-center lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-complement-800 via-complement-950 to-[#070d11]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[20%] top-1/2 h-[min(500px,55vw)] w-[min(500px,55vw)] -translate-y-1/2 rounded-full bg-complement-500/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-[10%] h-72 w-72 rounded-full bg-complement-400/12 blur-[80px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-20%,rgba(255,255,255,0.05),transparent_55%)]"
          aria-hidden
        />

        <motion.div
          className="relative mx-auto max-w-[600px]"
          initial={reduceMotion ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-300/75">
            Your next step
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold tracking-tight text-white">
            See what an operator-led partnership looks like for your environment
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-white/60">
            Get a 15-minute walkthrough of how we&apos;d approach the specific
            constraints of your health system, network, or portfolio.
          </p>
          <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              size="lg"
              className="h-[54px] border-0 bg-white px-10 text-[16px] font-bold text-complement-950 shadow-lg shadow-black/25 hover:bg-complement-50"
            >
              <Link to="/contact">
                Book Your 15-Minute Strategy Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="mt-4 text-[12px] font-medium text-white/40">
            No obligation. No pitch deck. Just a conversation with an operator.
          </p>
        </motion.div>
      </section>
    </>
  );
}
